import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  ProductStoreProduct,
  FetchProductStoreProductsRequest,
  FetchProductStoreProductsResponse,
} from "../api/fetchProductStoreProducts/types";
import { AppDispatch, AppThunk } from "@/app/store";
import { fetchProductStoreProducts } from "../api/fetchProductStoreProducts/fetchProductStoreProductsApi";
import { fetchProductStoreProduct } from "../api/fetchProductStoreProduct/fetchProductStoreProductApi";
import { createProductStoreProduct } from "../api/createProductStoreProduct/createProductStoreProductApi";
import { updateProductStoreProduct } from "../api/updateProductStoreProduct/updateProductStoreProductApi";
import { deleteProductStoreProduct } from "../api/deleteProductStoreProduct/deleteProductStoreProductApi";
import { CreateProductStoreProductRequest } from "../api/createProductStoreProduct/types";

interface ProductStoreProductsState {
  products: ProductStoreProduct[];
  totalCount: number;
  loading: boolean;
  error: string | null;
  currentProduct: ProductStoreProduct | null;
}

const initialState: ProductStoreProductsState = {
  products: [],
  totalCount: 0,
  loading: false,
  error: null,
  currentProduct: null,
};

const productStoreProductsSlice = createSlice({
  name: "productStoreProducts",
  initialState,
  reducers: {
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    setProductsData: (
      state,
      action: PayloadAction<{
        products: ProductStoreProduct[];
        meta: { totalCount: number };
      }>,
    ) => {
      state.products = action.payload.products;
      state.totalCount = action.payload.meta.totalCount;
    },
    addProduct: (state, action: PayloadAction<ProductStoreProduct>) => {
      state.products.push(action.payload);
      state.totalCount += 1;
    },
    updateProductInState: (
      state,
      action: PayloadAction<ProductStoreProduct>,
    ) => {
      const index = state.products.findIndex((p) => p.id === action.payload.id);
      if (index !== -1) {
        state.products[index] = action.payload;
      }
    },
    removeProduct: (state, action: PayloadAction<string>) => {
      state.products = state.products.filter((p) => p.id !== action.payload);
      state.totalCount -= 1;
    },
    setCurrentProduct: (
      state,
      action: PayloadAction<ProductStoreProduct | null>,
    ) => {
      state.currentProduct = action.payload;
    },
    resetProductStoreProducts: () => initialState,
  },
});

export const {
  setLoading,
  setError,
  setProductsData,
  addProduct,
  updateProductInState,
  removeProduct,
  setCurrentProduct,
  resetProductStoreProducts,
} = productStoreProductsSlice.actions;

export const fetchProductStoreProductsAction =
  (
    requestData: FetchProductStoreProductsRequest,
    signal?: AbortSignal,
  ): AppThunk =>
  async (dispatch: AppDispatch) => {
    dispatch(setLoading(true));
    try {
      const response: FetchProductStoreProductsResponse =
        await fetchProductStoreProducts(requestData, signal);

      const products = response.data || [];
      const totalCount = response.meta?.totalCount || 0;

      dispatch(
        setProductsData({
          products,
          meta: { totalCount },
        }),
      );
    } catch (error) {
      if (error instanceof Error && error.name === "CanceledError") {
        return;
      }
      if (error instanceof Error) {
        dispatch(setError(error.message));
      } else {
        dispatch(setError("Failed to fetch product store products"));
      }
    } finally {
      dispatch(setLoading(false));
    }
  };

export const fetchProductStoreProductAction =
  (id: string): AppThunk =>
  async (dispatch: AppDispatch) => {
    dispatch(setLoading(true));
    try {
      const response = await fetchProductStoreProduct(id);
      dispatch(setCurrentProduct(response.data));
      return response.data;
    } catch (error) {
      if (error instanceof Error) {
        dispatch(setError(error.message));
      } else {
        dispatch(setError("Failed to fetch product store product"));
      }
      throw error;
    } finally {
      dispatch(setLoading(false));
    }
  };

export const createProductStoreProductAction =
  (data: CreateProductStoreProductRequest): AppThunk =>
  async (dispatch: AppDispatch) => {
    dispatch(setLoading(true));
    try {
      const response = await createProductStoreProduct(data);
      dispatch(addProduct(response.data));
      return response.data;
    } catch (error) {
      if (error instanceof Error) {
        dispatch(setError(error.message));
      } else {
        dispatch(setError("Failed to create product store product"));
      }
      throw error;
    } finally {
      dispatch(setLoading(false));
    }
  };

export const updateProductStoreProductAction =
  (id: string, data: CreateProductStoreProductRequest): AppThunk =>
  async (dispatch: AppDispatch) => {
    dispatch(setLoading(true));
    try {
      const response = await updateProductStoreProduct(id, data);
      dispatch(updateProductInState(response.data));
      return response.data;
    } catch (error) {
      if (error instanceof Error) {
        dispatch(setError(error.message));
      } else {
        dispatch(setError("Failed to update product store product"));
      }
      throw error;
    } finally {
      dispatch(setLoading(false));
    }
  };

export const deleteProductStoreProductAction =
  (id: string): AppThunk =>
  async (dispatch: AppDispatch) => {
    dispatch(setLoading(true));
    try {
      await deleteProductStoreProduct(id);
      dispatch(removeProduct(id));
    } catch (error) {
      if (error instanceof Error) {
        dispatch(setError(error.message));
      } else {
        dispatch(setError("Failed to delete product store product"));
      }
      throw error;
    } finally {
      dispatch(setLoading(false));
    }
  };

export default productStoreProductsSlice.reducer;
